import { useRipple } from "@chaocore/ripple";
import CookieType, { CookieOptions } from "@constants/CookieType";
import PaymentGateway from "@constants/PaymentGateway";
import CountriesIso2 from "@data/iso2-countries.json";
import Languages from "@data/languages.json";
import Regions from "@data/regions.json";
import CountriesTimeZone from "@data/regions-to-countries.json";
import type Language from "@interfaces/Language";
import type NetworkCarrier from "@interfaces/NetworkCarrier";
import type Product from "@interfaces/Product";
import type Region from "@interfaces/Region";
import tailwindConfig from "@root/tailwind.config";
import { useAccountDetailStore } from "@stores/account_detail";
import { useAuthenticationStore } from "@stores/authentication";
import { useConfigurationStore } from "@stores/configuration";
import { usePaymentStore } from "@stores/payment";
import axios from "@utilities/axios";
import { getRootDomain } from "@utilities/base-url";
import api from "@utilities/fetchClient";
import { getLandingPage } from "@utilities/landing-page-tracking";
import { allowedGateways } from "@utilities/paymentConfiguration";
import { getCountryContent } from "@utilities/pricing-data";
import { type ClassValue, clsx } from "clsx";
import { type TFunction } from "i18next";
import Cookies from "js-cookie";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import resolveConfig from "tailwindcss/resolveConfig";

export interface RegionItem {
  link: string;
  name: string;
}

export const cn = (...inputs: ClassValue[]): string => {
  return twMerge(clsx(inputs));
};

export const isDebugMode = (): boolean => process.env.GATSBY_DEBUG === "true";

// Source of truth for RTL is `languages.json` (see also `seo.tsx`).
// Returns true when the given language id is configured as right-to-left.
export const isRtlLanguage = (language: string): boolean => {
  const entry = (Languages as Language[]).find(l => l.id === language);
  return entry?.is_rtl === true;
};

// The live site answers on several hosts — www.bnesim.com, bnesim.com and the
// shop.bnesim.com alias, plus the bnesim.shop (V2) and bne.li (V3) families —
// so "production" is any host on one of the configured root domains, not just
// the one in `getSiteUrl()`. Localhost and Netlify deploy previews / branch
// deploys (*.netlify.app) never match.
export const isProductionMode = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location?.hostname ?? "";
  if (hostname === "") return false;
  return [
    process.env.GATSBY_ROOT_DOMAIN,
    process.env.GATSBY_ROOT_DOMAIN_V2,
    process.env.GATSBY_ROOT_DOMAIN_V3,
  ].some(
    rootDomain =>
      rootDomain !== undefined &&
      rootDomain !== "" &&
      (hostname === rootDomain || hostname.endsWith(`.${rootDomain}`)),
  );
};

// Beta ("Test Mode") gate. Anything that isn't the live site — local dev,
// Netlify deploy previews, branch deploys — is implicitly in beta; on
// production it has to be unlocked deliberately via `?set-beta=1` (see
// `beta_mode.tsx`), which drops the `is_beta` cookie.
export const isBetaMode = (): boolean => {
  if (!isProductionMode()) return true;
  const betaCookie = Cookies.get(CookieType.IS_BETA);
  if (!betaCookie) return false;
  return parseInt(betaCookie) === 1;
};

export const setBetaMode = (value: boolean): void => {
  if (value) {
    Cookies.set(CookieType.IS_BETA, "1", CookieOptions);
  } else {
    Cookies.remove(CookieType.IS_BETA, {
      domain: getRootDomain(),
    });
  }
};

// ArcoPay terminal for this session: the test one (when configured) whenever
// beta / TEST MODE is on -- previews, localhost, or the live site after
// ?set-beta=1 -- and the live one otherwise. PTS picks the matching Bearer token
// from the stripe_key row, so both work against the same API.
export const getArcoPayPublishableKey = (): string => {
  const liveKey = process.env.GATSBY_ARCOPAY_ID ?? "";
  const testKey = process.env.GATSBY_TEST_ARCOPAY_ID ?? "";
  return isBetaMode() && testKey !== "" ? testKey : liveKey;
};

// CloudPayments terminal for this session, chosen the same way as ArcoPay's:
// the test Public ID (when configured) in beta / TEST MODE, the live one
// otherwise. Each must match an active `gateway='cloud-payments'` stripe_key row.
export const getCloudPaymentsPublishableKey = (): string => {
  const liveKey = process.env.GATSBY_CLOUDPAYMENTS_ID ?? "";
  const testKey = process.env.GATSBY_TEST_CLOUDPAYMENTS_ID ?? "";
  return isBetaMode() && testKey !== "" ? testKey : liveKey;
};

export const getPaymentPublishableKey = (): string => {
  const paymentState = usePaymentStore.getState();

  if (!allowedGateways.includes(paymentState.gateway)) {
    return "";
  }
  switch (paymentState.gateway) {
    case PaymentGateway.STRIPE:
      return process.env.GATSBY_STRIPE_ID ?? "";
    case PaymentGateway.ARCOPAY:
      return getArcoPayPublishableKey();
    case PaymentGateway.CLOUD_PAYMENTS:
      return getCloudPaymentsPublishableKey();
    default:
      return "";
  }
};
export const getAvailablePaymentPublishableKeys = (): Array<
  Record<string, string>
> => {
  const publishableKeys: Array<Record<string, string>> = [];
  allowedGateways.forEach(gateway => {
    let value = "";
    switch (gateway) {
      case PaymentGateway.STRIPE:
        value = process.env.GATSBY_STRIPE_ID ?? "";
        break;
      case PaymentGateway.ARCOPAY:
        value = getArcoPayPublishableKey();
        break;
      case PaymentGateway.CLOUD_PAYMENTS:
        value = getCloudPaymentsPublishableKey();
        break;
    }
    if (value) {
      publishableKeys.push({ [gateway]: value });
    }
  });

  return publishableKeys;
};

export const defaultRipple = useRipple({});

export const primaryRipple = useRipple({
  color: resolveConfig(tailwindConfig).theme.colors.primary.DEFAULT,
});

export const getDurationText = (t: TFunction, product: Product): string => {
  let durationText = "";
  if (parseInt(product.duration.toString()) > 0) {
    if (product.has_subscription) {
      durationText = t("monthly");
    } else {
      durationText = product.duration + " " + t("days");
    }
  } else {
    durationText = t("no_expiration");
  }

  return durationText;
};

/**
 * Normalize a plan's data allowance to megabytes so plans with mixed units
 * (MB / GB) can be compared consistently. Uses 1 GB = 1024 MB.
 */
export const getPlanDataInMB = (product: Product): number => {
  const amount = Number(product.amount) || 0;
  return product.unit?.toUpperCase() === "GB" ? amount * 1024 : amount;
};

/**
 * Comparator that sorts plans by data allowance ascending (1 GB, 3 GB, 5 GB...).
 * Falls back to price ascending when two plans have the same data amount.
 */
export const comparePlansByData = (a: Product, b: Product): number => {
  const dataDiff = getPlanDataInMB(a) - getPlanDataInMB(b);
  if (dataDiff !== 0) return dataDiff;

  const priceA = parseFloat(a.price) || 0;
  const priceB = parseFloat(b.price) || 0;
  return priceA - priceB;
};

/**
 * Build the de-duplicated carrier list for a single product, matching how the
 * site renders networks elsewhere (see `plan_details.tsx`): carrier names come
 * from `product.carriers` (comma-split), logo/speed from `product.networks[0]`
 * by index. No filtering — all carriers are shown.
 */
export const getProductNetworkCarriers = (
  product: Product | null,
): NetworkCarrier[] => {
  const networkCarriers: NetworkCarrier[] = [];
  if (!product) {
    return networkCarriers;
  }
  const networks = product.networks?.[0] ?? [];
  const carriers =
    product.carriers?.flatMap(item => item.split(",").map(str => str.trim())) ??
    [];
  carriers.forEach((carrier: string, index: number) => {
    const network = networks[index];
    if (
      network &&
      !networkCarriers.some(
        item => item.name === carrier && item.logo === network.logo,
      )
    ) {
      networkCarriers.push({
        name: carrier,
        logo: network.logo,
        best_network_speed: network.best_network_speed,
      });
    }
  });
  return networkCarriers;
};

export const handleRedirectToApp = (email = ""): void => {
  window.open(
    process.env.GATSBY_ONBOARDING_URL + "&user_email=" + email,
    "_blank",
  );
};

export const handleRedirectToSIMApp = (email = ""): void => {
  window.open(
    process.env.GATSBY_SIM_ONBOARDING_URL + "&user_email=" + email,
    "_blank",
  );
};

export const clearUserData = (): void => {
  useAccountDetailStore.getState().clear();
  useAuthenticationStore.getState().clear();
  usePaymentStore.getState().clear();
  useConfigurationStore.getState().reset();

  const rootDomain = getRootDomain();
  Cookies.remove(CookieType.CURRENCY, {
    domain: rootDomain,
  });
  Cookies.remove(CookieType.TOKEN, {
    domain: rootDomain,
  });
  Cookies.remove(CookieType.ACCOUNT_NUMBER, {
    domain: rootDomain,
  });
  Cookies.remove(CookieType.HASHED_PASSWORD, {
    domain: rootDomain,
  });
  Cookies.remove(CookieType.AFFILIATE, {
    domain: rootDomain,
  });

  api.put("/logout").catch(error => {
    console.error("[helper] Logout error:", error);
  });
};

export const range = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

type CountryType = Record<string, string>;
export const getRealLocalCountryCode = (): string => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localCountry = (CountriesTimeZone as CountryType)[timezone];
  const localCountryCode = (CountriesIso2 as CountryType)[localCountry];

  return localCountryCode;
};

export const getTranslatedLocationName = (
  value: string,
  language: string,
): string => {
  // Regions
  const region = (Regions as Region[]).find(
    (region: Region) => region.slug === value,
  );
  if (region) {
    return region.name[language] ?? region.name.en ?? "";
  }

  // Countries
  return getCountryContent(value, language)?.country_name ?? "";
};

export const createPaymentIntent = async (
  product: Product | null,
  currency: string,
  actionOverride?: "sim" | "esim",
): Promise<
  | { paymentIntentId: string; clientSecret: string; paymentUrl: string }
  | undefined
> => {
  if (!product) {
    return;
  }
  const paymentState = usePaymentStore.getState();
  const accountDetailState = useAccountDetailStore.getState();

  const formData = new FormData();
  formData.append("product", product.product);
  formData.append("currency", currency);
  formData.append("bundle_name", process.env.GATSBY_BUNDLE_NAME ?? "");
  formData.append("publishable_key", getPaymentPublishableKey());

  const action = actionOverride ?? (product.esim ? "esim" : "sim");
  if (action === "esim") {
    formData.append("action", "esim");
  } else {
    formData.append("action", "sim");
    formData.append("address", "no_address");
    formData.append("shipment", "express");
    formData.append("method", product.currency);
  }

  if (product.offer_type === "eligible-for-free") {
    formData.append("free_product", "1");
  }

  const url = "/payment_methods/";
  try {
    const response = await axios.post(url, formData);

    if (isDebugMode()) {
      console.log("JSON response:", response.data);
    }
    // The API answers `transaction: false` when the gateway refused to create
    // the order -- ArcoPayClient::createPaymentIntent() returns false when
    // /payments/create fails or is rejected. Reading `.id` off that yields
    // undefined and the caller then treats it as a generic failure, so name it
    // here: the actual reason is server-side only, written to the PTS log and
    // to credit_card_transaction.bank_response.
    const transaction: any = response.data.transaction;
    if (typeof transaction !== "object" || transaction === null) {
      console.error(
        "[helper]",
        "[payment] the gateway did not create an order (transaction=" +
          JSON.stringify(transaction) +
          ", publishable_key " +
          getPaymentPublishableKey() +
          "). Check the PTS log for the gateway client's Response line, or " +
          "credit_card_transaction.bank_response on the newest row for this license.",
      );
      return;
    }
    // Annotated because `response.data` is `any`: without these the values flow
    // untyped into the store setters.
    const paymentIntentId: string = transaction.id ?? "";
    const clientSecret: string = transaction.client_secret ?? "";
    // Redirect gateways (ArcoPay) return a hosted payment-form URL instead of a
    // client secret -- the customer pays on the gateway's page, not ours.
    const paymentUrl: string = transaction.payment_url ?? "";
    if (paymentIntentId === "") {
      return;
    }
    if (paymentState.gateway === PaymentGateway.ARCOPAY && paymentUrl === "") {
      // The order was created, but not by ArcoPay -- so there is no hosted form
      // to send the customer to, and "Pay" can only fail. Name the cause here
      // rather than let it surface as a generic "Payment failed" on click.
      //
      // `getPaymentClient()` selects BOTH gateway and merchant account from the
      // publishable_key, and falls back to StripeClient when no row matches. A
      // client_secret coming back alongside a missing payment_url is that
      // fallback's signature.
      console.error(
        "[helper]",
        "[ArcoPay] payment_methods returned no payment_url (intent " +
          paymentIntentId +
          ", publishable_key " +
          getPaymentPublishableKey() +
          (clientSecret !== ""
            ? ", client_secret present -> the backend fell back to Stripe"
            : "") +
          "). Expected an active `stripe_key` row with gateway='arcopay' and " +
          "that publishable_key -- see pts/migrations/migration_arcopay_gateway.sql.",
      );
    }
    if (
      paymentState.gateway === PaymentGateway.CLOUD_PAYMENTS &&
      clientSecret !== ""
    ) {
      // CloudPayments mints its intent locally and never returns a secret; one
      // here means `getPaymentClient()` matched no active cloud-payments row
      // and fell back to Stripe, so the widget would charge an order PTS
      // cannot confirm.
      console.error(
        "[helper]",
        "[CloudPayments] payment_methods returned a Stripe intent (intent " +
          paymentIntentId +
          ", publishable_key " +
          getPaymentPublishableKey() +
          "). Expected an active `stripe_key` row with gateway='cloud-payments' " +
          "and that publishable_key.",
      );
      return;
    }
    // Stripe Elements cannot confirm without the secret, so an empty one is
    // still fatal there. Other gateways legitimately have none.
    if (paymentState.gateway === PaymentGateway.STRIPE && clientSecret === "") {
      return;
    }
    paymentState.setPaymentIntentId(paymentIntentId);
    paymentState.setClientSecret(clientSecret);
    paymentState.setPaymentUrl(paymentUrl);
    accountDetailState.updatePaymentMethods(
      response.data.cards,
      response.data.wallets,
      response.data.coupons,
    );
    return {
      paymentIntentId,
      clientSecret,
      paymentUrl,
    };
  } catch (error: any) {
    if (error.response) {
      console.error(`[helper] Response status: ${error.response.status}`);
    } else {
      console.error("[helper]", error.message);
    }
  }
};

// `saveCard`: ArcoPay's "Save card" choice; left out, the backend saves the
// card as before.
export const preProcessPayment = async (saveCard?: boolean): Promise<void> => {
  const today = moment().format("YYYY-MM-DD");
  const paymentState = usePaymentStore.getState();

  const formData = new FormData();
  formData.append("action", "update-parameters");
  formData.append("piid", paymentState.paymentIntentId);
  formData.append(
    "request_smart_top_up",
    paymentState.isSmartTopUpChecked ? "1" : "0",
  );
  if (saveCard !== undefined) {
    formData.append("save_card", saveCard ? "1" : "0");
  }
  if (paymentState.scheduleDate && paymentState.scheduleDate !== today) {
    formData.append("scheduled_date", paymentState.scheduleDate);
  }
  // The best-esim-for-* page this visitor last landed on; pts stores it on the
  // order for the admin "Landing page stats" report.
  const landingPage = getLandingPage();
  if (landingPage !== null) {
    formData.append("landing_page", landingPage);
  }
  formData.append("bundle_name", process.env.GATSBY_BUNDLE_NAME ?? "");
  formData.append("publishable_key", getPaymentPublishableKey());

  const url = "/payment_methods/";

  const response = await axios.post(url, formData);
  if (isDebugMode()) {
    console.log("JSON response:", response.data);
  }
};

export const getStoreIcons = (
  language: string,
): {
  appStore: string;
  playStore: string;
  microsoftStore: string;
  macAppStore: string;
} => {
  const lang =
    language === "ar" ||
    language === "de" ||
    language === "es" ||
    language === "fr" ||
    language === "it" ||
    language === "ja" ||
    language === "kr" ||
    language === "ru" ||
    language === "zh" ||
    language === "tr"
      ? language
      : "en";

  return {
    appStore: `/images/stores/${lang}/app_store_icon.svg`,
    playStore: `/images/stores/${lang}/play_store_icon.svg`,
    microsoftStore: `/images/stores/${lang}/microsoft_store_icon.svg`,
    macAppStore: `/images/stores/${lang}/mac_app_store_icon.svg`,
  };
};

export const normalize = (value: string): string => {
  return value
    .normalize("NFD") // Split letters from accents
    .replace(/[\u0300-\u036f]/g, "") // Remove accent marks
    .toLowerCase();
};

export const getCsrfToken = () => {
  if (typeof document === "undefined") {
    return null;
  }
  const match = document.cookie
    .split("; ")
    .find(row => row.startsWith("XSRF-TOKEN="));

  return match ? decodeURIComponent(match.split("=")[1]) : null;
};

export const scrollToTop = () => {
  if (typeof window !== "undefined" && window.scrollTo) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
};
